import React from 'react';
import { useQueryClient } from 'react-query';
import { EntityNameModal } from '../EntityNameModal/EntityNameModal';
import { useCreateFolderMutation } from '../../api/useCreateFolderMutation';
import { useRouteParams } from '../../hooks/useRouteParams';
import { EntityTypeDTO, ProviderType } from '../../DocumentVault.meta';
import { useNavigate } from '../../hooks/useNavigate';
import { useFilterableYearOption } from '../../state/selectors';
import { queryKeys } from '../../api/queryKeys';

interface Props {
  onClose(): void;
}

export const CreateFolder: React.FC<Props> = ({ onClose }) => {
  const { clientId, path, providerType, filterableByYear } = useRouteParams();

  const { value: year } = useFilterableYearOption();

  const queryClient = useQueryClient();

  const {
    mutateAsync: createFolder,
    isLoading: isCreatingFolder,
    error: createFolderError,
  } = useCreateFolderMutation();

  const navigate = useNavigate();

  const handleSubmit = async (folderName: string) => {
    if (providerType === ProviderType.CloudStorage) {
      // The folder is created when a file is added to it.
      navigate({
        path: `${path}${encodeURIComponent(`${folderName}/`)}`,
        providerType,
      });

      // Invalidate the parent folder's cache to ensure the newly created folder appears
      // in the list after a file is uploaded.
      queryClient.invalidateQueries(queryKeys.listDocumentVaultEntities(clientId, path));

      onClose();
    } else {
      const folderPath = `${decodeURIComponent(path)}${folderName}/`;

      try {
        const currentYear = filterableByYear ? year : null;
        await createFolder({
          clientId,
          folderData: {
            folderPath,
            path,
            providerType,
            currentYear,
          },
        });

        queryClient.refetchQueries(
          queryKeys.listDocumentVaultEntities(clientId, path, providerType, currentYear),
          {
            active: true,
          },
        );

        onClose();
      } catch {
        // Ignore
      }
    }
  };

  return (
    <EntityNameModal
      mode="create"
      entityType={EntityTypeDTO.Folder}
      isLoading={isCreatingFolder}
      error={createFolderError}
      onSubmit={handleSubmit}
      onClose={onClose}
    />
  );
};
